import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { LoadingService } from '../loading/loading.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, firstValueFrom, Observable, tap, throwError } from 'rxjs';
import { AppMessageService } from '../app-message/app-message.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class FileApiService {
  private readonly baseURL = environment.fileApiUrl;
  private readonly headers = {};

  constructor(
    private readonly http: HttpClient,
    private readonly loadingService: LoadingService,
    private readonly appMessageService: AppMessageService,
    private readonly sanitizer: DomSanitizer
  ) {
    this.headers = { AcceptLanguage: 'tr' };
  }

  post<T>(
    apiUrl: string,
    body: { [key: string]: any } | FormData = {},
    loaderConfig = { showLoader: true, message: 'Loading...' },
    headers: { [key: string]: string } = { ...this.headers }
  ): Observable<T> {
    if (loaderConfig.showLoader) {
      this.presentLoading(loaderConfig.message);
    }
    return this.http
      .post<T>(`${this.baseURL}${apiUrl}`, body, {
        headers,
      })
      .pipe(
        tap((response) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleResponse(response);
        }),
        catchError((err) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleError(err);
          return throwError(()=> err);;
        })
      );
  }

  get<T>(
    apiUrl: string,
    parameters?: { [key: string]: string },
    loaderConfig: { showLoader: boolean; message?: string } = {
      showLoader: true,
      message: 'Loading...',
    },
    headers: { [key: string]: string } = {}
  ): Observable<T> {
    if (loaderConfig.showLoader) {
      this.presentLoading(loaderConfig?.message);
    }
    const params = new HttpParams({ fromObject: parameters });
    return this.http
      .get<T>(`${this.baseURL}${apiUrl}`, {
        params,
        headers,
      })
      .pipe(
        tap((response) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleResponse(response);
        }),
        catchError((err) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleError(err);
          return throwError(()=> err);
        })
      );
  }

  put<T = any>(
    apiUrl: string,
    body: { [key: string]: any } | FormData = {},
    loaderConfig = { showLoader: true, message: 'Loading...' },
    headers: { [key: string]: string } = {}
  ): Observable<T> {
    if (loaderConfig.showLoader) {
      this.presentLoading(loaderConfig.message);
    }
    return this.http
      .put<T>(`${this.baseURL}${apiUrl}`, body, {
        headers,
      })
      .pipe(
        tap((response) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleResponse(response);
        }),
        catchError((err) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleError(err);
           return throwError(()=> err);
        })
      );
  }

  patch<T = any>(
    apiUrl: string,
    body: { [key: string]: any } | FormData = {},
    loaderConfig = { showLoader: true, message: 'Loading...' },
    headers: { [key: string]: string } = {}
  ): Observable<T> {
    if (loaderConfig.showLoader) {
      this.presentLoading(loaderConfig.message);
    }
    return this.http
      .patch<T>(`${this.baseURL}${apiUrl}`, body, {
        headers,
      })
      .pipe(
        tap((response) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleResponse(response);
        }),
        catchError((err) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleError(err);
          return throwError(()=> err);;
        })
      );
  }

  delete<T = any>(
    apiUrl: string,
    parameters?: { [key: string]: any },
    loaderConfig: { showLoader: boolean; message?: string } = {
      showLoader: true,
      message: 'Loading...',
    },
    headers: { [key: string]: string } = {}
  ): Observable<T> {
    if (loaderConfig.showLoader) {
      this.presentLoading(loaderConfig.message);
    }
    const params = new HttpParams({ fromObject: parameters });
    return this.http
      .delete<T>(`${this.baseURL}${apiUrl}`, {
        params,
        headers,
      })
      .pipe(
        tap((response) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleResponse(response);
        }),
        catchError((err) => {
          if (loaderConfig.showLoader) {
            this.loadingService.hide();
          }
          this.HandleError(err);
          return throwError(()=> err);;
        })
      );
  }

  HandleError(error: any) {
    if (error?.error?.errors) {
      let errorMessage = '';
      for(const err of error.error.errors){
        for(const values of err.constraints){
            errorMessage += `${err.property} ${values[1]}` + '\n';
        }
      }
      this.appMessageService.errorToast(
        error.error.message,
        errorMessage,
        20000,
        'top-right'
      );
    } else if (typeof error?.error?.error == 'string') {
      this.appMessageService.errorToast(
        '',
        error.error.error,
        20000,
        'top-right'
      );
    } else {
      this.appMessageService.errorToast(
        error?.error,
        error?.error?.message,
        20000,
        'top-right'
      );
    }
  }

  HandleResponse(response: any) {
    if (response.Status === 500) {
      alert(response);
    }
  }

  async presentLoading(message?: string) {
    this.loadingService.show({
      message,
      spinner: 'bubbles'
    });
  }

  getFileBlob(url: string, token: string): Observable<Blob> {
    return this.http
      .get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => {
          console.error('Blob fetch error:', error);
          return throwError(() => new Error('Belge alınamadı'));
        })
      );
  }

  async getFileSafeUrl(url: string, token: string): Promise<SafeUrl> {
    try {
      const blob = await firstValueFrom(this.getFileBlob(url, token));

      if (!blob || !(blob instanceof Blob)) {
        throw new Error('Geçersiz dosya verisi');
      }

      const objectUrl = URL.createObjectURL(blob);
      return this.sanitizer.bypassSecurityTrustUrl(objectUrl);
    } catch (error) {
      console.error('Safe URL oluşturulamadı:', error);
      return this.sanitizer.bypassSecurityTrustUrl('assets/no-image.png');
    }
  }
}
